import { Box } from '@mui/material';

import { useFormikContext } from 'formik';
import { useState } from 'react';
import { ERPJobFormValues } from '@/raptorjob/presentation/ERPJobValidationSchema';
import type { NamedEntity } from '@/raptorjob/application/utils/jobDataExtractor';
import { JOB_FORM_MODES, type JobFormMode } from '@/raptorjob/domain/constants/jobFormLabels';
import { ERPCustomerInfoSection } from './sections/ERPCustomerInfoSection';
import { ERPJobInfoSection } from './sections/ERPJobInfoSection';
import { ERPDispatchTimesSection } from './sections/ERPDispatchTimesSection';
import { ERPEstimatesSection } from './sections/ERPEstimatesSection';
import { ERPNotesSection } from './sections/ERPNotesSection';
import { ERPSalesInformationSection } from './sections/ERPSalesInformationSection';
import { ERPDispatchInformationSection } from './sections/ERPDispatchInformationSection';
import { ERPFlagsSection } from './sections/ERPFlagsSection';
import { isFieldRequired, REQUIRED_FIELD_NAMES } from '@/raptorjob/application/utils/erpSettings';
import CustomerModal from '@/customers/presentation/pages/CustomersPage/CustomersManagementTab/CustomerModal';
import type { CallerDto } from '@/libs/api-rest/generated/model';

interface ERPJobInfoStepProps {
  jobSiteSearchTerm: string;
  setJobSiteSearchTerm: (term: string) => void;
  setSelectedCustomerName?: (term: string) => void;
  mode?: JobFormMode;
  rawCaller?: string | null;
  callerInfo?: CallerDto | null;
  vendorId?: number | null;
  vendor?: NamedEntity | null;
  dispatcher?: NamedEntity | null;
  dispatchLocation?: NamedEntity | null;
  salesAgentId?: number | null;
  salesAgent?: string | null;
  jobType?: NamedEntity | null;
  jobSite?: { id: number; name: string; address?: string | null } | null;
  flag1?: NamedEntity | null;
  flag2?: NamedEntity | null;
  flag3?: NamedEntity | null;
  availableDispatchLocations?: Array<{ id: number; name?: string | null; default?: boolean | null }>;
  requiredFields?: Array<{ name: string; required: boolean }>;
  jobEndDateActive: boolean;
  setJobEndDateActive: (active: boolean) => void;
  setFieldVisible?: (field: 'showJobEndDate' | 'showJobEndTime', visible: boolean) => void;
  onPORequiredChange?: (poRequired: boolean) => void;
}

export const ERPJobInfoStep = ({
  jobSiteSearchTerm,
  setJobSiteSearchTerm,
  setSelectedCustomerName,
  mode = JOB_FORM_MODES.CREATE,
  rawCaller,
  callerInfo,
  vendor,
  dispatcher,
  dispatchLocation,
  salesAgentId,
  salesAgent,
  jobType,
  jobSite,
  flag1,
  flag2,
  flag3,
  availableDispatchLocations = [],
  requiredFields = [],
  jobEndDateActive,
  setJobEndDateActive,
  setFieldVisible,
  onPORequiredChange
}: ERPJobInfoStepProps) => {
  const { setFieldValue, setFieldTouched, setFieldError } = useFormikContext<ERPJobFormValues>();
  const [isCreateCustomerModalOpen, setIsCreateCustomerModalOpen] = useState(false);

  const isVolumeRequired = isFieldRequired(requiredFields, REQUIRED_FIELD_NAMES.VOLUME);
  const isCallerRequired = isFieldRequired(requiredFields, REQUIRED_FIELD_NAMES.CALLER);

  const handleCreateCustomerSuccess = (customer: { id: number; companyName: string }) => {
    setFieldError('customerId', undefined);
    setFieldTouched('customerId', false, false);
    setFieldValue('customerId', customer.id, false);
    setFieldValue('customerName', customer.companyName, false);
    setSelectedCustomerName?.(customer.companyName);
    setIsCreateCustomerModalOpen(false);
  };

  return (
    <Box>
      <ERPCustomerInfoSection
        onAddNewCustomer={() => setIsCreateCustomerModalOpen(true)}
        onCustomerChanged={() => {
          setFieldValue('jobSiteId', null);
          setJobSiteSearchTerm('');
          setFieldValue('callerId', null);
        }}
        setSelectedCustomerName={setSelectedCustomerName}
        mode={mode}
        rawCaller={rawCaller}
        callerInfo={callerInfo}
        callerRequired={isCallerRequired}
      />
      <ERPJobInfoSection
        jobSiteSearchTerm={jobSiteSearchTerm}
        onJobSiteSearchTermChange={setJobSiteSearchTerm}
        mode={mode}
        vendor={vendor}
        jobType={jobType}
        jobSite={jobSite}
      />
      <ERPDispatchTimesSection
        mode={mode}
        jobEndDateActive={jobEndDateActive}
        setJobEndDateActive={setJobEndDateActive}
        setFieldVisible={setFieldVisible}
      />
      <ERPEstimatesSection volumeRequired={isVolumeRequired} />
      <ERPSalesInformationSection salesAgentId={salesAgentId} salesAgent={salesAgent} mode={mode} onPORequiredChange={onPORequiredChange} />
      <ERPDispatchInformationSection
        dispatcher={dispatcher}
        dispatchLocation={dispatchLocation}
        availableDispatchLocations={availableDispatchLocations}
      />
      <ERPFlagsSection flag1={flag1} flag2={flag2} flag3={flag3} />
      <ERPNotesSection />
      <CustomerModal
        open={isCreateCustomerModalOpen}
        onClose={() => setIsCreateCustomerModalOpen(false)}
        onSuccess={handleCreateCustomerSuccess}
      />
    </Box>
  );
};
